import { z } from 'zod'
import { IField, IPrice } from '@/client/types'
import { ErrorMessages } from '@/client/types/Calculator'
import {
  Button,
  Extra,
  Resume,
  Step6,
  Step7,
  Help,
  CalculatorStep,
  RandomText
} from '@/client/types/TransparencyCalculator'

export type FieldType =
  | 'select-radio'
  | 'input-number'
  | 'select-radio-vertical'
  | 'textarea'
  | 'checkbox'
  | 'range'
  | 'select-card-checkbox'
  | 'select-radio-vertical'

export type CalculatorProps = {}

export type Selection = {
  [key: string]: string | string[] | number | number[]
}

export type UploadedFileSummary = {
  name: string
  numCodes: number
  price: number
  cartFileItem: string
}

export type SettingsChildProps = {
  fields: IField[]
  texts: Step6
  step?: number
  randomText?: RandomText
  reset?: boolean
  setResetFalse?: () => void
  sharedState: {
    tpyLang: string
    setTpyLang: (lang: string) => void
    tpyCta: string
    setTpyCta: (cta: string) => void
    tpyLayout: string
    setTpyLayout: (layout: string) => void
    tpyPaper: string
    setTpyPaper: (paper: string) => void
    uploadedFilesDetailsFromSummary: UploadedFileSummary[]
    setUploadedFilesDetails: (uploadFiles: UploadedFileSummary[]) => void
  }
}

export enum Steps {
  SPECIFICATIONS = 1,
  MATERIAL_AND_TYPE = 2,
  LANGUAGE_AND_BRAND_ID = 3,
  MECHINE_PROCESSING = 4,
  CSV_UPLOAD = 5,
  SKU_INPUT = 6,
  SUMMARY = 7
}

export enum LabelTypes {
  LABEL_TYPE_1 = 1, // 30mm x 30mm
  LABEL_TYPE_2 = 2, // 45mm x 20mm
  LABEL_TYPE_3 = 3, // 35mm x 35mm
  LABEL_TYPE_4 = 4, // 100mm x 35mm
  LABEL_TYPE_6 = 6, // 50mm x 25mm
  LABEL_TYPE_7 = 7, // standard seal
  LABEL_TYPE_8 = 8 // plus seal
}

export type StepStylesProps = {
  active: boolean
  finished?: boolean
}

export type SummaryChildProps = {
  calculatorFields: IField[]
  optionFields: IField[]
  texts: Step7
  randomText?: RandomText
  production_time: IPrice
  sharedState: {
    tpyLang: string
    setTpyLang: (lang: string) => void
    tpyCta: string
    setTpyCta: (cta: string) => void
    tpyLayout: string
    setTpyLayout: (layout: string) => void
    tpyPaper: string
    setTpyPaper: (paper: string) => void
    uploadedFilesDetailsFromSummary: UploadedFileSummary[]
    setUploadedFilesDetails: (uploadFiles: UploadedFileSummary[]) => void
  }
}

export type ResultProps = {
  result: IPrice
  step: Steps
  setStep: (step: Steps) => void
  setIsAlertOpen: (isAlertOpen: boolean) => void
  fields: IField[]
  texts: Resume
  error_messages?: ErrorMessages
}

export type ResultFooterProps = {
  texts: {
    button: Button
    extra: Extra
    help: Help
    more_info: {
      info_sentence: string
    }
    footer_message: string
  }
}

/*
 *
 * Calculator fields based on each steps are handled here.
 * Fetch calculator fields from price API based on the below steps and fields.
 *
 */

export const getCalculatorConstants = (): CalculatorStep[] => [
  {
    step: Steps.SPECIFICATIONS,
    fields: [
      { key: 'tpy_layout' },
      { key: 'quantity' },
      { key: 'number_of_sku' },
      { key: 'tpy_cta' },
      { key: 'label_type' }
    ]
  },
  {
    step: Steps.MATERIAL_AND_TYPE,
    fields: [{ key: 'tpy_layout' }, { key: 'paper' }, { key: 'tpy_type' }]
  },
  {
    step: Steps.LANGUAGE_AND_BRAND_ID,
    fields: [{ key: 'tpy_layout' }, { key: 'tpy_lang' }, { key: 'tpy_brand' }]
  },
  {
    step: Steps.MECHINE_PROCESSING,
    fields: [
      { key: 'tpy_layout' },
      { key: 'machine_processing' },
      { key: 'winding_direction' },
      { key: 'core_diameter' },
      { key: 'assembly' }
    ]
  },
  {
    step: Steps.CSV_UPLOAD,
    fields: [{ key: 'tpy_layout' }]
  },
  {
    step: Steps.SKU_INPUT,
    fields: [{ key: 'tpy_layout' }]
  }
]

export const schema = (texts: ErrorMessages | undefined) => {
  const formSchema = z.object({
    quotation_cust_name: z.string().min(1, texts?.required),
    quotation_cust_mail: z.string().email(texts?.invalid_email)
  })
  return formSchema
}
